///<amd-module name = "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/Components/PredefinedQuestionButtons"/>
import * as React from "react";
import { 
    displayedPredefinedQuestionTranslationTags, 
    shouldIApproveThisInvoice,
    summarizeTheInvoiceContext, 
    tellMeMoreAboutTheSupplier
} from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/AgentConsts";
import { getLabelTranslation } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import { Button, Card, IconActionPinFill, IconActionUnpinFill, IconArrowsChevronDownRegular, IconArrowsChevronUpRegular, Text } from "@medius/ui-controls";
import { useCopilotContext } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/hooks/useCopilotContext";
import { IPredefinedQuestion } from "Core/Medius.Core.Web/Scripts/Medius/apps/task/Agent/agentInterfaces";

function getIsPinnedFromLocalStorage(){
    const storageValue = localStorage.getItem("pinnedExpandedPredefinedQuestions");
    if(storageValue === "expanded" || storageValue === "collapsed")
        return true;
    return false;
}

function subscribeToLocalStorageChanges(callback: () => void) {
    window.addEventListener("pinnedExpandedPredefinedQuestionsUpdated", callback);

    return () => {
        window.removeEventListener("pinnedExpandedPredefinedQuestionsUpdated", callback);
    };
}

interface PredefinedQuestionsProps {
    disabled: boolean,
    predefinedQuestionsList: IPredefinedQuestion[],
    onPredefinedQuestionClick: (predefinedQuestion: string) => Promise<void>,
}

export const PredefinedQuestions = ({
    onPredefinedQuestionClick,
    predefinedQuestionsList,
    disabled
}: PredefinedQuestionsProps) => {
    const { workflowStepName, isStreamActive } = useCopilotContext();
    const [isCardOpen, setIsCardOpen] = React.useState(localStorage.getItem("pinnedExpandedPredefinedQuestions") !== "collapsed");
    const areExpandedPredefinedQuestionsPinned = React.useSyncExternalStore(subscribeToLocalStorageChanges, getIsPinnedFromLocalStorage);

    //Card should hide when response stream starts unless it's pinned.
    const [prevIsStreamActive, setPrevIsStreamActive] = React.useState(null);
    if(prevIsStreamActive !== isStreamActive) { 
        setPrevIsStreamActive(isStreamActive);
        if(isStreamActive && !areExpandedPredefinedQuestionsPinned && isCardOpen) {
            setIsCardOpen(false);
        }
    }

    const toggleCardHeader = () => {
        setIsCardOpen(prev => !prev);
    };

    const displayedPredefinedQuestions = predefinedQuestionsList.filter(question => 
        displayedPredefinedQuestionTranslationTags.includes(question.value)
    );

    const canBeExpanded = displayedPredefinedQuestions.length > 2;

    return displayedPredefinedQuestions.length > 0 ?
        <Card 
            onOpenToggle={() => {}}
            isCollapsible 
            height="content"
            open={isCardOpen && canBeExpanded}
            appearTransition={false} 
            renderCustomHeader={() => (
                <CustomCardHeaderContent 
                    isCardOpen={isCardOpen && canBeExpanded} 
                    disabled={disabled} 
                    onPredefinedQuestionClick={onPredefinedQuestionClick}
                    stepName={workflowStepName} 
                    toggleCardHeader={toggleCardHeader}
                    pinnedState={areExpandedPredefinedQuestionsPinned}
                    isExpandable={canBeExpanded}
                    displayedPredefinedQuestions={displayedPredefinedQuestions}
                />
            )}
        >
            <div className="mediusAgent__promptInput__predefinedQuestions" data-testid="predefinedQuestions-container">
                {displayedPredefinedQuestions.map((question, index) => 
                    <div key={index} >
                        <Button
                            label={getLabelTranslation(question.value)}
                            height="small"
                            variant="tertiary"
                            onClick={() => onPredefinedQuestionClick(question.value)}
                            disabled={disabled}
                            data-testid={`predefinedQuestion-${question.categorizationId}`}
                        />
                    </div>
                )}
            </div>
    </Card> : 
    <></>;
};

interface ICardHeaderCustomProps{
    stepName: string, 
    disabled: boolean,
    onPredefinedQuestionClick: (predefinedQuestion: string) => Promise<void>
    isCardOpen: boolean;
    toggleCardHeader?: () => void;
    pinnedState: boolean;
    isExpandable?: boolean;
    displayedPredefinedQuestions: IPredefinedQuestion[];
}

function CustomCardHeaderContent({ disabled, onPredefinedQuestionClick, isCardOpen, toggleCardHeader, pinnedState, isExpandable, displayedPredefinedQuestions }: ICardHeaderCustomProps){
    const getHeaderClass = () => {
        let headerClass = "mediusAgent__promptInput__cardHeaderPredefinedQuestions";
        if(isCardOpen)
            headerClass+= " mediusAgent__promptInput__cardHeaderPredefinedQuestions__fadeout";
        return headerClass;
    };

    const updateLocalStorageItem = (value: string | null) => {
        if(!value) {
            localStorage.removeItem("pinnedExpandedPredefinedQuestions");
        } else {
            localStorage.setItem("pinnedExpandedPredefinedQuestions", value);
        }
        window.dispatchEvent(new Event("pinnedExpandedPredefinedQuestionsUpdated"));
    };

    const handleCardHeaderToggle = () => {
        if(isExpandable) {
            updateLocalStorageItem(null);
            toggleCardHeader();
        }
    };

    const pinExpandedPredefinedQuestions = () => {
        if(pinnedState){
            updateLocalStorageItem(null);
            return;
        }
        if(isCardOpen){
            updateLocalStorageItem("expanded");
        } else {
            updateLocalStorageItem("collapsed");
        }
    };

    const getHeaderPredefinedQuestions = React.useCallback(() => {
        let filteredQuestions = displayedPredefinedQuestions.filter(q => q.value === shouldIApproveThisInvoice || q.value === summarizeTheInvoiceContext);
        if(filteredQuestions.length === 2) {
            return filteredQuestions;
        }

        filteredQuestions = displayedPredefinedQuestions.filter(q => q.value === summarizeTheInvoiceContext || q.value === tellMeMoreAboutTheSupplier);
        if(filteredQuestions.length === 2) {
            return filteredQuestions;
        }

        if(displayedPredefinedQuestions.length >= 2) {
            return [displayedPredefinedQuestions[0], displayedPredefinedQuestions[1]];
        }

        return displayedPredefinedQuestions.map(q => q);
    }, [displayedPredefinedQuestions]);

    const headerPredefinedQuestions = getHeaderPredefinedQuestions();

    return (
        <div className="mediusAgent__promptInput__predefinedQuestions__customHeaderContainer" data-testid="mediusAgent__predefinedQuestions__cardHeaderContainer">
            <div className="mediusAgent__promptInput__predefinedQuestions__customHeaderContainer__innerContainer">
                <div className="mediusAgent__promptInput__predefinedQuestions__customHeaderContainer__innerContainer__titlePinContainer">
                    <Text variant="heading3" style={{marginRight: "5px"}}>
                        {getLabelTranslation("#Core/PredefinedPrompts")}
                    </Text>
                    <div 
                         data-testid="predefinedQuestions-cardHeaderPin"
                        onClick={pinExpandedPredefinedQuestions} 
                        aria-label={pinnedState ? getLabelTranslation("#Core/hideExpandedPredefinedQuestions") : getLabelTranslation("#Core/pinExpandedPredefinedQuestions")}
                        style={{cursor: "pointer", display: "flex"}}
                    >
                        {pinnedState ? <IconActionUnpinFill  /> : <IconActionPinFill />}
                    </div>
                </div>
                {isExpandable && 
                    <div
                        className="mediusAgent__promptInput__toggleIcon"
                        data-testid="predefinedQuestions-cardHeaderToggle"
                        onClick={handleCardHeaderToggle}
                        aria-label={isCardOpen ? getLabelTranslation("#Core/SeeLessPredefinedPrompts") : getLabelTranslation("#Core/SeeMorePredefinedPrompts")}
                        style={{cursor: isExpandable?"pointer":"", display: "flex"}}
                    >
                        {isCardOpen ? <IconArrowsChevronDownRegular /> : <IconArrowsChevronUpRegular />}
                    </div>
                }
            </div>
            <div className={getHeaderClass()}>
            {
                headerPredefinedQuestions.map((q) => {
                    return <Button 
                        key={q.categorizationId} 
                        data-testid={`header-predefinedQuestion-${q.categorizationId}`}
                        onClick={() => onPredefinedQuestionClick(q.value)} 
                        label={getLabelTranslation(q.value)} 
                        variant="tertiary" 
                        height="small" 
                        disabled={disabled}
                    />;
                })
            }</div>
        </div>
    );
}